import qs from 'query-string';
import { useCallback, useEffect, useRef, useState } from 'react';
import useBaseAPI from './useBaseAPI';

const useUserAPI = () => {
  const { client, isLoading } = useBaseAPI();

  const getUsers = async (query) => {
    try {
      const response = await client('get', `/user?${qs.stringify(query)}`);
      return response?.data;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const getUser = async (userId) => {
    try {
      const response = await client('get', `/user/${userId}`);
      return response?.data;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const addUser = async (payload) => {
    try {
      const response = await client('post', '/user', payload);
      return response;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const updateUser = async (userId, payload) => {
    try {
      const response = await client('post', `/user/${userId}`, payload);
      return response;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await client('delete', `/user/${userId}`);
      return response?.success;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const getDropDownOptions = async () => {
    try {
      const response = await client('get', `/user/customer-role-project`);
      return response;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const isUserNameAvailable = async (username) => {
    try {
      const response = await client('get', `/user/username/available/${username}`);
      return response;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const getUsersPaginated = async (query) => {
    try {
      const response = await client('get', `/user?${qs.stringify(query)}`);
      return response?.data;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const saveFeedback = async (payload) => {
    try {
      const response = await client('post', `/user/feedback`, payload);
      return response;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const downloadUserList = async () => {
    try {
      const response = await client('get', `/Audit/User/Report`, null, {
        accept: 'text/csv',
      });
      return response;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  return {
    getUsers,
    getUser,
    addUser,
    updateUser,
    deleteUser,
    getDropDownOptions,
    isUserNameAvailable,
    getUsersPaginated,
    saveFeedback,
    downloadUserList,
    isLoading,
  };
};

export default useUserAPI;

export const useUserHook = () => {
  const [users, setUsers] = useState();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [customerId, setCustomerId] = useState();
  const [status, setStatus] = useState();
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  const { getUsersPaginated } = useUserAPI();

  const isInitialLoad = useRef(true);

  useEffect(() => {
    fetchUsers();
  }, [page, customerId, status]);

  useEffect(() => {
    if (isInitialLoad.current) return;

    let searchStringTimeoutId = null;

    searchStringTimeoutId = setTimeout(() => {
      setUsers(undefined);
      setPage(1);
      if (page === 1) fetchUsers();
    }, 1000);

    return () => clearTimeout(searchStringTimeoutId);
  }, [searchString]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    }
  }, []);

  const fetchUsers = async () => {
    setIsPaginationLoading(true);
    const query = { search: searchString, page, customerId, status, limit: 20 };
    const usersResponse = await getUsersPaginated(query);
    setUsers(page === 1 ? usersResponse.data : [...users, ...usersResponse.data]);
    setHasMore(usersResponse?.haveMore);
    setIsPaginationLoading(false);
  };

  const observer = useRef();

  const lastElementRef = useCallback((node) => {
    if (isPaginationLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPageNumber) => prevPageNumber + 1);
      }
    });
    if (node) observer.current.observe(node);
  });

  return {
    users,
    searchString,
    setSearchString,
    setPage,
    lastElementRef,
    isPaginationLoading,
    setCustomerId,
    customerId,
    fetchUsers,
    setStatus,
    setUsers,
    status,
  };
};
